import DisplayItem from '@/models/DisplayItem';
import Section from '@/models/Section';
import Test from '@/models/Test';
import DeliveryOptionConstants from '../options/DeliveryOptionConstants';
import TestController from '../TestController';
import INavigationController from './INavigationController';
import NavigationContext from './state/NavigationContext';

type SectionChangeCallbackFunction = () => void;
class NavigationController implements INavigationController {
  private readonly items: Array<DisplayItem>;
  private index = 0;
  private lockSection = false;
  private logoutSection = false;
  private _testController: TestController;
  private sectionChangeCallback: SectionChangeCallbackFunction | undefined;
  private _navigationContext: NavigationContext | undefined;

  constructor(itemArray: Array<DisplayItem>, testController: TestController) {
    this.items = itemArray;
    this.index = this.findStartingIndex(this.items);
    this._testController = testController;
    this.setOptions();
  }

  isBeginningOfTest(): boolean {
    return this.index === 0;
  }

  set navigationContext(navigationContext: NavigationContext | undefined) {
    this._navigationContext = navigationContext;
  }

  get navigationContext(): NavigationContext | undefined {
    return this._navigationContext;
  }

  setSectionChangeCallback(callback: (() => void) | undefined) {
    this.sectionChangeCallback = callback;
  }

  setOptions(): void {
    this.lockSection = this._testController
      ? this.testController.deliveryOptionEnabledForTest(DeliveryOptionConstants.BLOCK_RETURN_TO_SECTION)
      : false;
    this.logoutSection = this._testController
      ? this.testController.deliveryOptionEnabledForTest(DeliveryOptionConstants.LOGOUT_SECTION)
      : false;
  }

  get testController(): TestController {
    return this._testController;
  }

  getCurrentItem(): DisplayItem | null {
    return this.index < this.items.length ? this.items[this.index] : null;
  }

  getNextItem(): Promise<DisplayItem | null> {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    return new Promise<DisplayItem | null>((resolve) => {
      const previousSection: Section | undefined = that.getCurrentItem() ? that.getCurrentItem()!.section : undefined;
      that.index++;

      that.checkForSectionChange(previousSection, that.getCurrentItem() ? that.getCurrentItem()!.section : undefined);

      resolve(that.getCurrentItem());
    });
  }

  getPreviousItem(): DisplayItem | null {
    const previousSection: Section | undefined = this.getCurrentItem() ? this.getCurrentItem()!.section : undefined;
    this.index--;

    this.checkForSectionChange(previousSection, this.getCurrentItem() ? this.getCurrentItem()!.section : undefined);

    return this.getCurrentItem();
  }

  setCurrentItem(item: DisplayItem): void {
    if (item) {
      const previousSection: Section | undefined = this.getCurrentItem() ? this.getCurrentItem()!.section : undefined;

      this.index = this.items.findIndex((itemInArray) => itemInArray == item);

      this.checkForSectionChange(previousSection, this.getCurrentItem() ? this.getCurrentItem()!.section : undefined);
    }
  }

  previousAvailable(): boolean {
    if (this.lockSection && this.index > 0 && this.index < this.items.length) {
      return this.items[this.index].section == this.items[this.index - 1].section;
    }
    return this.index > 0;
  }

  getItemArray(): Array<DisplayItem> {
    if (this.items[this.index]) {
      const beginningIndex = this.items.findIndex((item) => this.items[this.index].section == item.section);
      const endingIndex = this.items.findIndex(
        (item, index) => index > beginningIndex && item.section != this.items[this.index].section
      );

      return this.items.slice(beginningIndex, endingIndex !== -1 ? endingIndex : this.items.length + 1);
    }
    return new Array<DisplayItem>();
  }

  getAllItems(): Array<DisplayItem> {
    return this.items;
  }

  getDisplayItems(): Array<DisplayItem> {
    return this.items;
  }

  beginningOfSection(): boolean {
    return this.getCurrentItem()?.section != this.peekPreviousItem()?.section;
  }

  endOfSection(): boolean {
    const nextItem: DisplayItem | null = this.peekNextItem();
    return !nextItem || nextItem.section != this.getCurrentItem()!.section;
  }

  endOfTest(): boolean {
    return !this.peekNextItem();
  }

  sectionLogout(): boolean {
    return this.logoutSection;
  }

  showBackButton(): boolean {
    return this.getCurrentItem() !== null && this.getCurrentItem()!.section.testPart.navigationMode === 'nonlinear';
  }

  setIndex(item: DisplayItem | undefined): void {
    if (item) {
      const index: number = this.items.findIndex((currentItem) => currentItem == item);
      if (index != -1) {
        this.index = index;
      }
    }
  }

  peekNextItem(): DisplayItem | null {
    return this.index + 1 < this.items.length ? this.items[this.index + 1] : null;
  }

  peekPreviousItem(): DisplayItem | null {
    return this.index - 1 >= 0 ? this.items[this.index - 1] : null;
  }

  lastSectionInTest(): boolean {
    if (this.index < this.items.length) {
      return this.items[this.index].section == this.items[this.items.length - 1].section;
    } else {
      return true;
    }
  }

  private findStartingIndex(itemArray: Array<DisplayItem>): number {
    const currentItemArray: Array<DisplayItem> = itemArray.filter((item) => item.currentItem === true);
    let index = currentItemArray.length === 1 ? itemArray.findIndex((item) => item.currentItem) : 0;
    if (index > 0) {
      if (itemArray[index].section.testPart.test.locationState === Test.SECTION_INSTRUCTIONS_STATE) {
        index = this.findFirstInstructionIndex(itemArray, itemArray[index].section, index);
      }
    }
    return index;
  }

  private checkForSectionChange(previousSection: Section | undefined, nextSection: Section | undefined): void {
    if (this.sectionChangeCallback && previousSection != nextSection) {
      this.sectionChangeCallback();
    }
  }

  private findFirstInstructionIndex(itemArray: Array<DisplayItem>, section: Section, index: number): number {
    const newIndex: number = index - 1;
    if (newIndex > 0 && itemArray[newIndex].section == section) {
      return this.findFirstInstructionIndex(itemArray, section, newIndex);
    } else {
      return newIndex === 0 ? newIndex : newIndex + 1;
    }
  }
}

export default NavigationController;
